/**
 * TaxonomyFieldEditor component - Edit a single taxonomy field
 * Handles field name, guidance, options, and parent-child relationships
 */

import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { ChevronDown, ChevronUp, GripVertical, Plus, Trash2 } from 'lucide-react';
import type { HTMLAttributes } from 'react';
import { useState } from 'react';

import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { cn } from '@/lib/utils';
import type { TaxonomyField, TaxonomyOption } from '@/types/taxonomy';

import { SortableTaxonomyOption } from './SortableTaxonomyOption';

// ============================================================================
// TYPES
// ============================================================================

export interface TaxonomyFieldEditorProps {
  field: TaxonomyField;
  onChange: (field: TaxonomyField) => void;
  onRemove: () => void;
  allFields: TaxonomyField[];
  isLoading?: boolean;
  errors?: {
    name?: string;
    options?: string;
    parentFieldId?: string;
  };
  dragHandleProps?: HTMLAttributes<HTMLDivElement>;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function generateOptionId(): string {
  return `opt_${Math.random().toString(36).substring(2, 12)}`;
}

// ============================================================================
// COMPONENT
// ============================================================================

export function TaxonomyFieldEditor({
  field,
  onChange,
  onRemove,
  allFields,
  isLoading = false,
  errors = {},
  dragHandleProps,
}: TaxonomyFieldEditorProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [showParentConfig, setShowParentConfig] = useState(!!field.parentFieldId);

  // Drag-and-drop sensors for options
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleOptionDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = field.options.findIndex((o) => o.optionId === active.id);
      const newIndex = field.options.findIndex((o) => o.optionId === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const reorderedOptions = arrayMove(field.options, oldIndex, newIndex);
        onChange({ ...field, options: reorderedOptions });
      }
    }
  };

  // Get potential parent fields (fields with lower position)
  const potentialParents = allFields.filter(
    (f) => f.fieldId !== field.fieldId && f.position < field.position
  );

  // Get parent field if set
  const parentField = field.parentFieldId
    ? allFields.find((f) => f.fieldId === field.parentFieldId)
    : null;

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const handleNameChange = (name: string) => {
    onChange({ ...field, name });
  };

  const handleGuidanceChange = (guidance: string) => {
    onChange({ ...field, guidance: guidance || undefined });
  };

  const handleAddOption = () => {
    const newOption: TaxonomyOption = {
      optionId: generateOptionId(),
      label: '',
    };
    onChange({
      ...field,
      options: [...field.options, newOption],
    });
  };

  const handleUpdateOption = (index: number, option: TaxonomyOption) => {
    const newOptions = [...field.options];
    newOptions[index] = option;
    onChange({ ...field, options: newOptions });
  };

  const handleRemoveOption = (index: number) => {
    const optionToRemove = field.options[index];
    if (!optionToRemove) return;

    const newOptions = field.options.filter((_, i) => i !== index);
    // Also remove from parentOptionConditions if present
    const optionId = optionToRemove.optionId;
    let newConditions = field.parentOptionConditions;
    if (newConditions && newConditions[optionId]) {
      newConditions = { ...newConditions };
      delete newConditions[optionId];
    }
    onChange({
      ...field,
      options: newOptions,
      parentOptionConditions: newConditions,
    });
  };

  const handleParentFieldChange = (parentFieldId: string | undefined) => {
    if (parentFieldId) {
      // Initialize conditions for all options to empty arrays
      const conditions: Record<string, string[]> = {};
      field.options.forEach((opt) => {
        conditions[opt.optionId] = [];
      });
      onChange({
        ...field,
        parentFieldId,
        parentOptionConditions: conditions,
      });
    } else {
      onChange({
        ...field,
        parentFieldId: undefined,
        parentOptionConditions: undefined,
      });
    }
  };

  const handleParentConditionToggle = (optionId: string, parentOptionId: string) => {
    const currentConditions = field.parentOptionConditions || {};
    const optionConditions = currentConditions[optionId] || [];

    let newConditions: string[];
    if (optionConditions.includes(parentOptionId)) {
      newConditions = optionConditions.filter((id) => id !== parentOptionId);
    } else {
      newConditions = [...optionConditions, parentOptionId];
    }

    onChange({
      ...field,
      parentOptionConditions: {
        ...currentConditions,
        [optionId]: newConditions,
      },
    });
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="border border-border-subtle rounded-lg bg-surface">
      {/* Header */}
      <div className="flex items-center gap-3 p-4 border-b border-border-subtle">
        <div
          className="cursor-grab text-muted hover:text-secondary touch-none"
          {...dragHandleProps}
        >
          <GripVertical className="h-5 w-5" />
        </div>

        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex-1 flex items-center gap-2 text-left"
        >
          <span className="font-semibold text-primary">
            {field.name || 'Untitled Field'}
          </span>
          <span className="text-xs text-muted">
            ({field.options.length} option{field.options.length !== 1 ? 's' : ''})
          </span>
          {isExpanded ? (
            <ChevronUp className="h-4 w-4 text-muted ml-auto" />
          ) : (
            <ChevronDown className="h-4 w-4 text-muted ml-auto" />
          )}
        </button>

        <button
          onClick={onRemove}
          className="p-2 rounded text-muted hover:text-danger hover:bg-danger/10 transition-colors"
          disabled={isLoading}
          aria-label={`Remove field ${field.name}`}
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="p-4 space-y-6">
          {/* Field Name */}
          <Input
            label="Field Name"
            value={field.name}
            onChange={(e) => handleNameChange(e.target.value)}
            placeholder="e.g., Production Style, Creative Route"
            disabled={isLoading}
            error={!!errors.name}
            errorMessage={errors.name}
            required
          />

          {/* Guidance */}
          <div>
            <label className="mb-2 block text-sm font-medium text-secondary">
              AI Guidance (optional)
            </label>
            <textarea
              value={field.guidance || ''}
              onChange={(e) => handleGuidanceChange(e.target.value)}
              placeholder="Help the AI understand how to classify this field. E.g., 'UGC means handheld camera with natural lighting...'"
              rows={3}
              maxLength={1000}
              disabled={isLoading}
              className="flex w-full font-sans text-md text-primary placeholder:text-muted transition-all duration-base ease-standard focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 rounded-md border border-border-subtle bg-surface px-4 py-3 focus-visible:border-brand/55 focus-visible:ring-4 focus-visible:ring-brand/12 resize-none"
            />
            <p className="mt-1 text-xs text-muted">
              {(field.guidance?.length || 0)}/1000 characters
            </p>
          </div>

          {/* Options */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-secondary">Options</label>
              <Button
                onClick={handleAddOption}
                variant="ghost"
                size="sm"
                leftIcon={<Plus className="h-3 w-3" />}
                disabled={isLoading}
              >
                Add Option
              </Button>
            </div>

            {errors.options && (
              <p className="text-xs text-danger">{errors.options}</p>
            )}

            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleOptionDragEnd}
            >
              <SortableContext
                items={field.options.map((o) => o.optionId)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-2">
                  {field.options.map((option, index) => (
                    <SortableTaxonomyOption
                      key={option.optionId}
                      option={option}
                      onChange={(opt) => handleUpdateOption(index, opt)}
                      onRemove={() => handleRemoveOption(index)}
                      isLoading={isLoading}
                    />
                  ))}

                  {field.options.length === 0 && (
                    <div className="text-center py-6 text-sm text-muted border-2 border-dashed border-border-subtle rounded-lg">
                      No options yet. Click &quot;Add Option&quot; to create one.
                    </div>
                  )}
                </div>
              </SortableContext>
            </DndContext>
          </div>

          {/* Parent-Child Relationship (only show if there are potential parents) */}
          {potentialParents.length > 0 && (
            <div className="space-y-3 pt-4 border-t border-border-subtle">
              <button
                onClick={() => setShowParentConfig(!showParentConfig)}
                className="flex items-center gap-2 text-sm font-medium text-secondary hover:text-primary transition-colors"
              >
                {showParentConfig ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
                Configure Parent-Child Relationship
              </button>

              {showParentConfig && (
                <div className="space-y-4 pl-6">
                  <div>
                    <label className="mb-2 block text-sm font-medium text-secondary">
                      Parent Field
                    </label>
                    <select
                      value={field.parentFieldId || ''}
                      onChange={(e) =>
                        handleParentFieldChange(e.target.value || undefined)
                      }
                      disabled={isLoading}
                      className="flex w-full h-11 font-sans text-md text-primary transition-all duration-base ease-standard focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 rounded-md border border-border-subtle bg-surface px-4 focus-visible:border-brand/55 focus-visible:ring-4 focus-visible:ring-brand/12"
                    >
                      <option value="">No parent (independent field)</option>
                      {potentialParents.map((pf) => (
                        <option key={pf.fieldId} value={pf.fieldId}>
                          {pf.name || 'Untitled Field'}
                        </option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-muted">
                      Child options will only be available when specific parent options are selected
                    </p>
                  </div>

                  {/* Parent Option Conditions */}
                  {field.parentFieldId && parentField && field.options.length > 0 && (
                    <div className="space-y-4">
                      <label className="text-sm font-medium text-secondary">
                        Option Conditions
                      </label>
                      <p className="text-xs text-muted -mt-2">
                        For each option, select which parent options enable it
                      </p>

                      {field.options.map((option) => (
                        <div
                          key={option.optionId}
                          className="p-3 rounded-md border border-border-subtle bg-surface-subtle space-y-2"
                        >
                          <span className="text-sm font-medium text-primary">
                            {option.label || '(unnamed)'}
                          </span>
                          <div className="flex flex-wrap gap-2">
                            {parentField.options.map((parentOpt) => {
                              const isSelected =
                                field.parentOptionConditions?.[option.optionId]?.includes(
                                  parentOpt.optionId
                                );
                              return (
                                <button
                                  key={parentOpt.optionId}
                                  onClick={() =>
                                    handleParentConditionToggle(
                                      option.optionId,
                                      parentOpt.optionId
                                    )
                                  }
                                  disabled={isLoading}
                                  className={cn(
                                    'px-3 py-1.5 text-xs rounded-pill border transition-colors',
                                    isSelected
                                      ? 'bg-brand/10 border-brand text-brand'
                                      : 'bg-surface border-border-subtle text-secondary hover:border-brand/50'
                                  )}
                                >
                                  {parentOpt.label || '(unnamed)'}
                                </button>
                              );
                            })}
                          </div>
                          {(!field.parentOptionConditions?.[option.optionId] ||
                            (field.parentOptionConditions[option.optionId]?.length ?? 0) === 0) && (
                            <p className="text-xs text-warning">
                              Warning: No parent options selected. This option will never be available.
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
